import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { PaymentStepProps } from './MakePayment';
import { RadioButtons } from '../../../../components/radiobuttons/radiobuttons';
import FormTextField from '../../../../components/form/FormTextField';
import Button from '../../../../components/button/Button';
import { useTranslation } from 'react-i18next';
import { useIsFinancialInstitution } from '../helpers';
import { useGetFiatAccountDetails } from 'helpers/useGetFiatAccountDetails';
import { ProductType } from 'components/sideMenu/SideMenu';
import { transferTypesThatAllowBeneficiaryReference } from './ReviewPayment';

export const paymentRefTipForFI =
    'Information that will be provided to the beneficiary bank and should appear on their statement. Please note this text may be truncated or removed.';
export const paymentRefTipForNonFi = 'Message to be recieved by the beneficiary';
export const beneficiaryRefTip =
    "16-character reference which shows up on the recipient's bank statement";

type Props = PaymentStepProps & {
    paymentMethods: string;
    initialValues: Partial<PaymentDetailsForm>;
};

export type PaymentDetailsForm = {
    transferType: string;
    paymentReference: string;
    beneficiaryReference: string | null;
};

const validationSchema = Yup.object({
    paymentReference: Yup.string()
        .matches(
            /^[a-zA-Z0-9-\s]+$/,
            'Invalid characters. Only letters, numbers, hyphens, and spaces are allowed.'
        )
        .min(5, 'Payment reference must be at least 5 characters long')
        .max(120, 'Payment reference must not exceed 120 characters')
        .required('Please provide a payment reference'),
});

export const PaymentDetails: React.FC<Props> = ({ nextStep, paymentMethods, initialValues }) => {
    const accountDetails = useGetFiatAccountDetails();
    const isCrb = accountDetails?.productDisplayName === ProductType.CRB;
    const { t } = useTranslation('common');
    const bFinancialInstitution = useIsFinancialInstitution();

    const tooltip = !bFinancialInstitution ? paymentRefTipForNonFi : paymentRefTipForFI;

    const onSubmit = (values: PaymentDetailsForm) => {
        nextStep(values);
    };

    const showBeneficiaryReferenceForTransferType = (transferType: string) =>
        isCrb && transferTypesThatAllowBeneficiaryReference.includes(transferType);

    const allowedTransferTypes = paymentMethods.split(',').map((item) => item.trim()) ?? []; // allowedTransferTypes is a comma separated string e.g. "ACH, RTP, Wire"

    return (
        <Formik
            initialValues={{
                transferType: allowedTransferTypes[0],
                paymentReference: '',
                beneficiaryReference: null,
                ...initialValues,
            }}
            onSubmit={onSubmit}
            validationSchema={validationSchema}
        >
            {({ values }) => (
                <Form>
                    <RadioButtons
                        options={allowedTransferTypes}
                        fieldname="transferType"
                        label="Payment Method"
                        noTranslate
                    />
                    <FormTextField
                        field="paymentReference"
                        label="Payment Reference"
                        maxLength={!bFinancialInstitution ? 120 : 81}
                        tooltip={
                            showBeneficiaryReferenceForTransferType(values.transferType)
                                ? undefined
                                : tooltip
                        }
                    />
                    {showBeneficiaryReferenceForTransferType(values.transferType) && (
                        <FormTextField
                            field="beneficiaryReference"
                            label="Beneficiary Reference"
                            maxLength={16}
                            tooltip={beneficiaryRefTip}
                        />
                    )}
                    <Button type="submit" color="third">
                        {t('next')}
                    </Button>
                </Form>
            )}
        </Formik>
    );
};
